import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import {Component} from 'panel';

import {CONDITION_TYPE_TO_TAB_MAPPING, DEFAULT_PEOPLE_PROPERTY_OPTIONS} from '../../models/constants';
import {BuilderType, ConditionTab, ConditionType} from '../../models/enums/builder-enums';
import {LogicalOperator, Property} from '../../models/enums/expression-enums';
import {isCohortLiteral, isEventLiteral, renamePropertyValue} from '../../util/index';
import {PropertyType} from '../../../property-filter-menu/models/types';
import {TrackingEvent} from '../../util/tracking';
import customPropertiesStore from 'common/report/stores/custom-properties';

const OPERATOR_TO_OPERATOR_LABEL = {
  or: `any`,
  and: `all`,
};

export class ClauseComponent extends Component {
  shouldUpdate(state) {
    return (
      !!state.stagedCohort &&
      !!state.stagedCohort.literals[this.literalIdx] &&
      !!state.stagedCohort.literals[this.literalIdx].clauses[this.clauseIdx]
    );
  }

  get config() {
    return {
      useShadowDom: true,
      helpers: {
        BuilderType,
        isLastClause: () => this.isAttributeEnabled(`is-last-clause`),
        getLiteralIndex: () => this.literalIdx,
        getClauseIndex: () => this.clauseIdx,
        getClauseBuilderLabel: () => {
          let label;
          const clauseOperators = this.state.stagedCohort.getClauseOperators(this.literalIdx);
          if (!clauseOperators[0]) {
            label = `then`;
          } else if (!this.literalHasMultipleSubclauses) {
            label = clauseOperators[0];
          } else {
            label = `${clauseOperators[0]} ${OPERATOR_TO_OPERATOR_LABEL[clauseOperators[1]]}`;
          }

          return label;
        },
        getClauseOperatorHeader: () => {
          const secondOperator = this.state.stagedCohort.getClauseOperators(this.literalIdx)[1];
          const action = this.literal.isFunnel ? `all` : OPERATOR_TO_OPERATOR_LABEL[secondOperator];
          return this.literal.clauses.length < 3 || this.literal.isFunnel
            ? `${action} of the following`
            : `of the following`;
        },
        getJoinOperatorLabel: () => {
          let joinOperatorLabel;
          if (this.literal.isFunnel) {
            joinOperatorLabel = `and then`;
          } else if (!this.literalHasMultipleSubclauses) {
            joinOperatorLabel = null;
          } else {
            joinOperatorLabel = this.state.stagedCohort.getClauseOperators(this.literalIdx)[1];
          }

          return joinOperatorLabel;
        },
        getActionLabel: () => {
          throw new Error(`unimplemented`);
        },
        getActionOperatorLabel: () => {
          throw new Error(`unimplemented`);
        },
        getResourceLabel: () => {
          throw new Error(`unimplemented`);
        },
        isResourceValid: () => true,
        getInvalidResourceMessage: () => {
          throw new Error(`unimplemented`);
        },
        getPropertyFilterLabelParts: (propertyFilterIdx) => {
          const propertyFilter = this.state.stagedCohort.getProperty(this.clausePosition, propertyFilterIdx);
          const propertyName = propertyFilter.property;
          let labelParts = this.state.stagedCohort
            .getPropertyFilterLabelParts(this.clausePosition, propertyFilterIdx)
            .map((labelPart, idx) => {
              return idx === 0 ? labelPart : {...labelPart, label: renamePropertyValue(labelPart.label, propertyName)};
            });

          if (this.app.hasDataGroups() && propertyFilter.propertyType === Property.User) {
            const label = this.app.getDataGroupName(propertyFilter.dataGroupId);
            labelParts = [{label, isHighlighted: false}, ...labelParts];
          }

          return labelParts;
        },
        getClauseLabel: () => this.literal.getClauseLabel(this.clauseIdx),
        isNegated: () => this.literal.clauses[this.clauseIdx].isNegated(),
        addBuilderKeydownProxyEl: (vnode) => this.app.updateBuilderState({keydownProxyEl: vnode.elm}),
        toggleBuilder: (builderType) => {
          if (this.helpers.isBuilderOpen(builderType, this.clausePosition)) {
            this.app.closeBuilder(builderType, this.clausePosition);
          } else {
            this.helpers.startBuilder(builderType);
          }
        },
        handleClickConditionLabel: (ev) => {
          const conditionType = this.conditionType;
          if (this.helpers.isBuilderOpen(BuilderType.Condition)) {
            ev.stopPropagation(); // don't close the builder when it's open
            return;
          }

          const {clauseIdx} = this.clausePosition;
          if ([ConditionType.Cohorts, ConditionType.Events].includes(conditionType)) {
            this.helpers.startBuilder(BuilderType.Condition, {
              conditionTab: this.getBuilderConditionTab({clauseIdx, conditionType}),
              conditionTabType: this.getBuilderConditionTab({clauseIdx, conditionType}),
              propertyFilterIdx: null,
            });
          } else if (conditionType === ConditionType.People) {
            const propertyFilterIdx = 0;
            const propertyFilterOptions = pick(
              this.state.stagedCohort.getProperty(this.clausePosition, propertyFilterIdx),
              [`propertyType`, `property`, `type`, `operator`, `value`],
            );

            // for default value, start the builder at the first screen
            if (isEqual(propertyFilterOptions, DEFAULT_PEOPLE_PROPERTY_OPTIONS)) {
              this.helpers.startBuilder(BuilderType.Condition, {
                conditionTab: this.getBuilderConditionTab({clauseIdx, conditionType}),
                conditionTabType: this.getBuilderConditionTab({clauseIdx, conditionType}),
                propertyFilterIdx,
              });
            } else {
              const conditionTab = this.getBuilderConditionTab({
                clauseIdx: this.clausePosition.clauseIdx,
                conditionType,
              });
              this.startPropertyFilterBuilder({
                propertyFilterIdx,
                builderType: BuilderType.Condition,
                extraBuilderAttrs: {conditionTab, conditionTabType: conditionTab},
              });
            }
          }
        },
        selectedProperty: (ev) => {
          const {name, type, propertyType, dataGroupId, customPropertyId} = ev.detail.value;
          const {property} = this.updateStagedFilterClause({
            property: name,
            type,
            propertyType,
            allValues: [],
            loadingValues: true,
            value: null,
            filterSearch: ``,
            dataGroupId,
            customPropertyId,
          });

          if (type === PropertyType.Dimension) {
            this.fetchDimension(dataGroupId);
          } else {
            this.fetchPropertyValues(
              property,
              propertyType,
              dataGroupId,
              customPropertiesStore.value[customPropertyId],
            );
          }
        },
        selectedFilterOperator: (ev) => {
          this.updateStagedFilterClause({operator: ev.detail.value});
        },
        selectedFilterType: (ev) => {
          this.updateStagedFilterClause({type: ev.detail.value});
        },
        updatedFilterSearch: (ev) => {
          this.updateStagedFilterClause({filterSearch: ev.detail.value});
        },
        updatedFilterValue: (ev) => {
          const value = ev.detail.value;
          let allValues = this.stagedFilterClause.allValues;
          if (Array.isArray(value)) {
            // if new value "equals" value chosen, add it to the set of all values so it persists in the list
            allValues = Array.from(new Set([...allValues, ...value]));
          }

          this.updateStagedFilterClause({value, allValues});
        },
        searchFilterInput: (ev) => {
          this.app.updateBuilderState({searchFilter: ev.target.value});
        },
        startBuilder: (builderType, builderAttrs = {}) => {
          this.app.startClauseBuilderAtPane(`cohorts-builder-${builderType}`, builderType, this.clausePosition, {
            builderAttrs,
          });
        },
        isBuilderOpen: (builderType) =>
          this.app.isBuilderOpen(builderType, this.clausePosition, this.stagedPropertyFilterIdx),
        selectedClauseOperators: (ev) => {
          const clauseOperators = ev.detail.value;
          const {stagedCohort} = this.state;
          const [initialClauseOperator, joinClauseOperator] = stagedCohort.getClauseOperators(this.literalIdx);
          let trackingEvent;
          let newOperator;
          if (initialClauseOperator !== clauseOperators[0]) {
            trackingEvent = TrackingEvent.SelectInitialClauseOperator;
            newOperator = clauseOperators[0] || `then`;
          } else if (joinClauseOperator !== clauseOperators[1]) {
            trackingEvent = TrackingEvent.SelectJoinClauseOperator;
            newOperator = clauseOperators[1] === LogicalOperator.And ? `all` : `any`;
          }

          if (trackingEvent && newOperator) {
            this.app.track(
              trackingEvent,
              Object.assign({}, stagedCohort.getTrackingDataForLiteral(this.literalIdx), {operator: newOperator}),
            );
            const shouldCloseBuilder = !this.literalHasMultipleSubclauses || joinClauseOperator !== clauseOperators[1];
            if (shouldCloseBuilder) {
              this.updateStagedCohortAndCloseBuilder(
                BuilderType.ClauseOperator,
                stagedCohort.updateClauseOperators(clauseOperators, this.literalIdx),
              );
            } else {
              this.app.updateStagedCohort(stagedCohort.updateClauseOperators(clauseOperators, this.literalIdx));
            }
          } else {
            this.app.closeBuilder(BuilderType.ClauseOperator, this.clausePosition, this.stagedPropertyFilterIdx);
          }
        },
        selectedCohort: (ev) => {
          const {stagedCohort} = this.state;
          const cohort = this.app.getCohortById(ev.detail.value.id);
          // TODO(mack): Validate tracking with PM.
          const cohortProps = {
            'cohort id': cohort.id,
            'cohort name': cohort.name,
            'cohort editable': !cohort.locked,
            'cohort visible': !cohort.hidden,
          };
          let trackingEvent;
          let trackingData;
          if (!isCohortLiteral(this.literal)) {
            trackingEvent = TrackingEvent.ChangeConditionType;
            trackingData = Object.assign({}, stagedCohort.getTrackingDataForLiteral(this.literalIdx), {
              'condition type': `cohort`,
              ...cohortProps,
            });
          } else {
            trackingEvent = TrackingEvent.SelectCohort;
            const trackingProps = {...cohortProps};
            if (cohort.createdBy) {
              trackingProps[`cohort type`] = cohort.createdBy.id === this.attr(`user-id`) ? `cohorts-yours` : `cohorts`;
            }
            trackingData = Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), trackingProps);
          }

          this.app.track(trackingEvent, trackingData);
          this.updateStagedCohortAndCloseBuilder(
            BuilderType.Condition,
            stagedCohort.updateCohort(cohort.id, this.clausePosition),
          );
        },
        selectedEvent: (ev) => {
          const {stagedCohort} = this.state;
          const {custom, is_collect_everything_event, id, name} = ev.detail.value; // eslint-disable-line camelcase
          const eventName = custom || is_collect_everything_event ? `$custom_event:${id}` : name; // eslint-disable-line camelcase
          let trackingEvent;
          let trackingData;
          if (!isEventLiteral(this.literal)) {
            trackingEvent = TrackingEvent.ChangeConditionType;
            trackingData = Object.assign({}, stagedCohort.getTrackingDataForLiteral(this.literalIdx), {
              'condition type': `event`,
              event: eventName,
            });
          } else {
            trackingEvent = TrackingEvent.SelectEvent;
            trackingData = Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), {
              event: eventName,
            });
          }

          this.app.track(trackingEvent, trackingData);
          this.updateStagedCohortAndCloseBuilder(
            BuilderType.Condition,
            stagedCohort.updateEvent(eventName, this.clausePosition),
          );
        },
        savePropertyFilterAndCloseBuilder: (builderType) => {
          const stagedCohort = this.state.stagedCohort;
          const propertyFilterIdx = this.stagedPropertyFilterIdx;
          const newStagedCohort = stagedCohort.commitStagedFilterClause(this.clausePosition, propertyFilterIdx);
          let trackingEvent;
          let trackingData;
          if (propertyFilterIdx === null) {
            trackingEvent = TrackingEvent.ChangeConditionType;
            trackingData = Object.assign({}, stagedCohort.getTrackingDataForLiteral(this.literalIdx), {
              'condition type': `people`,
            });
          } else {
            if (stagedCohort.getNumPropertyFilters(this.clausePosition) === 0) {
              trackingEvent = TrackingEvent.AddPropertyFilter;
            } else if (this.behavior) {
              trackingEvent = TrackingEvent.UpdatePropertyFilter;
            } else {
              trackingEvent = TrackingEvent.UpdatePeopleClause;
            }

            trackingData = newStagedCohort.getTrackingDataForPropertyFilter(this.clausePosition, propertyFilterIdx);
          }

          this.app.track(trackingEvent, trackingData);
          this.updateStagedCohortAndCloseBuilder(builderType, newStagedCohort);
        },
        toggleActionOperator: () => {
          const {stagedCohort} = this.state;
          const negated = !this.helpers.isNegated();
          this.app.track(
            TrackingEvent.ToggleClauseNegated,
            Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), {negated}),
          );
          this.app.updateStagedCohort(stagedCohort.toggleClauseNegated(this.clausePosition));
        },
        removeClause: () => {
          const {stagedCohort} = this.state;
          this.app.track(TrackingEvent.RemoveClause, stagedCohort.getTrackingDataForClause(this.clausePosition));
          this.app.updateStagedCohort(stagedCohort.removeClause(this.clausePosition));
          this.app.update({builderScreen: this.app.defaultBuilderScreenState});
        },
      },
    };
  }

  replaceProperty(property, allValues) {
    let propertyValues = [];
    // ensure that all specified "equals" values are added to the list so they can be unselected
    const selectedPropertyFilter = this.state.stagedCohort.getProperty(
      this.clausePosition,
      this.stagedPropertyFilterIdx,
    );
    if (
      selectedPropertyFilter &&
      property === selectedPropertyFilter.property &&
      Array.isArray(selectedPropertyFilter.value)
    ) {
      propertyValues = selectedPropertyFilter.value;
    }

    this.updateStagedFilterClause({
      allValues: Array.from(new Set([...allValues, ...propertyValues])),
      loadingValues: false,
    });
  }

  fetchDimension(dataGroupId) {
    this.app.fetchDimensionProperties(dataGroupId);
  }

  fetchPropertyValues(property, propertyType, dataGroupId, customProperty = null) {
    this.app.fetchPropertyValues(property, propertyType, dataGroupId, customProperty).then((results) => {
      if (this.stagedFilterClause.property === property) {
        this.replaceProperty(property, results);
      }
    });
  }

  startPropertyFilterBuilder({propertyFilterIdx, builderType, leftOffset = `0px`, extraBuilderAttrs = {}}) {
    const {stagedCohort} = this.state;
    const propertyFilterOptions = pick(stagedCohort.getProperty(this.clausePosition, propertyFilterIdx), [
      `propertyType`,
      `property`,
      `type`,
      `operator`,
      `value`,
      `dataGroupId`,
      `customPropertyId`,
    ]);

    const stagedFilterClause = Object.assign({}, propertyFilterOptions, {allValues: [], loadingValues: true});
    this.app.updateStagedCohort(
      stagedCohort.updateStagedFilterClause(stagedFilterClause, this.clausePosition, propertyFilterIdx),
    );
    this.app.startClauseBuilderAtPane(`cohorts-builder-pane-filter-property`, builderType, this.clausePosition, {
      previousScreens: [`cohorts-builder-${builderType}`],
      builderAttrs: {propertyFilterIdx, leftOffset, ...extraBuilderAttrs},
    });

    this.fetchPropertyValues(
      stagedFilterClause.property,
      stagedFilterClause.propertyType,
      stagedFilterClause.dataGroupId,
      customPropertiesStore.value[stagedFilterClause.customPropertyId],
    );
  }

  updateStagedCohortAndCloseBuilder(builderType, stagedCohort) {
    this.app.closeBuilder(builderType, this.clausePosition, this.stagedPropertyFilterIdx);
    this.app.updateStagedCohort(stagedCohort);
  }

  updateStagedFilterClause(options) {
    const {stagedCohort} = this.state;
    this.app.updateStagedCohort(
      stagedCohort.updateStagedFilterClause(options, this.clausePosition, this.stagedPropertyFilterIdx),
    );

    return this.stagedFilterClause;
  }

  get conditionType() {
    throw new Error(`unimplemented`);
  }

  get literal() {
    return this.state.stagedCohort.literals[this.literalIdx];
  }

  get literalHasMultipleSubclauses() {
    return this.literal.clauses.length >= 3;
  }

  get stagedPropertyFilterIdx() {
    return this.state.builderScreen.propertyFilterIdx;
  }

  get stagedFilterClause() {
    return this.state.stagedCohort.getStagedFilterClause(this.state.stagedClausePosition, this.stagedPropertyFilterIdx);
  }

  get clausePosition() {
    return {
      literalIdx: this.literalIdx,
      clauseIdx: this.clauseIdx,
    };
  }

  getBuilderConditionTab({clauseIdx, conditionType}) {
    if (clauseIdx === 0) {
      return this.app.hasMultipleConditionTypes() ? ConditionTab.All : ConditionTab.Events;
    } else {
      return CONDITION_TYPE_TO_TAB_MAPPING[conditionType];
    }
  }

  get clauseIdx() {
    return Number(this.dataset.clauseIdx);
  }

  get literalIdx() {
    return Number(this.dataset.literalIdx);
  }
}
